import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from "@angular/core";
import { Router } from "@angular/router";
import { FileService } from "../../shared/services/file.service";
import { PDFProgressData, PDFDocumentProxy } from 'ng2-pdf-viewer';
import { AuditService } from "../services/audit.service";
import { EmployeeProcess } from "../models";
import { FileDocument } from "../models/file-document.model";
import { LsdService } from "../services/lsd.service";
import { extractPdfCertificates, PdfCertificateInfo } from './pdf-certificate-parser.util';
import { TranslocoService } from '@ngneat/transloco';

export interface PdfSignatureInfo {
  page: number;
  rect?: number[];
  hasAppearance: boolean;
  status: 'valid' | 'no-appearance' | 'unknown';
  signer?: string;
  cuil?: string;
  signedDate?: Date;
  role?: string;
  // Datos del "certificado" mostrados en el panel. Si hasEmbeddedCertificate es
  // true, provienen de un certificado X.509 real (CMS/PKCS7) leído del PDF. Si
  // es false, son un fallback armado con los datos de negocio disponibles
  // (firma electrónica sin certificado digital verificable).
  certSubject?: string;
  certIssuer?: string;
  certValidFrom?: Date;
  certValidTo?: Date;
  /** true si el PDF tiene un certificado X.509 real embebido (CMS/PKCS7), sea o no de un emisor licenciado. */
  hasEmbeddedCertificate?: boolean;
  /**
   * true solo si, además de tener certificado real, su emisor es una
   * Autoridad Certificante Licenciada por la ONTI (Ley 25.506): es la única
   * condición bajo la cual corresponde llamarla "Firma Digital" en sentido
   * legal. Un certificado X.509 real de un emisor no licenciado es "Firma
   * Electrónica" a pesar de tener certificado.
   */
  isDigitalCertificate?: boolean;
}

@Component({
  standalone: false,
  selector: "app-pdf-wrapper",
  templateUrl: "./pdf-wrapper.component.html",
  styleUrls: ["./pdf-wrapper.component.scss"]
})
export class PdfWrapperComponent implements OnChanges, OnDestroy {
  @Input() file: string;
  @Input() fileId: number;
  @Input() fileName: string;
  @Input() showControls: boolean;
  @Input() process: EmployeeProcess;
  @Input() doc: FileDocument;
  @Input() autoView: boolean;
  @Input() showAll = true;
  @Input() fileBlob: Blob;
  @Input() autoSize: boolean = false;
  @Input() hideSignaturePanel: boolean = false;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  pdfSrc: any;
  signing: boolean;
  signable: boolean;
  loading = true;
  loadError = false;
  pdfViewerVisible = false;
  pdfZoom = 1;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  pdf: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any;
  page = 1;
  isLoaded = false;
  progressData: PDFProgressData;
  size: number;
  pdfSignatures: PdfSignatureInfo[] = [];
  sigPanelExpanded = false;
  sigBannerVisible = true;
  // Bytes crudos del PDF actual, usados para leer el certificado real embebido en la firma
  private rawPdfBytes: ArrayBuffer;
  private pdfLoadVersion = 0;
  private objectUrl: string;

  /**
   * El panel de firma solo se muestra en los visualizadores del lado del
   * empleador (rutas /employer/...). Del lado del empleado no se muestra.
   */
  get showSignaturePanel(): boolean {
    return !this.hideSignaturePanel && this.router.url.startsWith('/employer');
  }

  get allSigsValid(): boolean {
    return this.pdfSignatures.length > 0 && this.pdfSignatures.every(s => s.status === 'valid');
  }

  /**
   * Evalúa, en base al certificado leído de cada firma, si el documento fue
   * firmado con certificado digital (X.509 real embebido), con firma
   * electrónica (sin certificado verificable) o con una combinación de ambos.
   */
  get signatureBannerText(): string {
    const count = this.pdfSignatures.length;
    if (count === 0) {
      return '';
    }

    const digitalCount = this.pdfSignatures.filter(s => s.isDigitalCertificate).length;

    let key: string;
    if (digitalCount === count) {
      key = count === 1 ? 'bannerOneDigitalLbl' : 'bannerManyDigitalLbl';
    } else if (digitalCount === 0) {
      key = count === 1 ? 'bannerOneElectronicLbl' : 'bannerManyElectronicLbl';
    } else {
      key = 'bannerMixedLbl';
    }

    return this.transloco.translate('pdfWrapper.pdfWrapper.' + key, { count, digitalCount });
  }

  constructor(
    private fileService: FileService,
    private auditService: AuditService,
    private lsdServices: LsdService,
    private readonly transloco: TranslocoService,
    private router: Router
  ) {

  }


  ngOnChanges(_changes: SimpleChanges): void {
    const loadVersion = ++this.pdfLoadVersion;
    this.revokeObjectUrl();
    this.pdfSrc = null;
    this.rawPdfBytes = undefined;
    this.pdfSignatures = [];
    this.isLoaded = false;
    this.loadError = false;
    this.pdfViewerVisible = false;

    if ((this.file != null && this.file.length > 0) || this.fileBlob) {
      this.refreshPDF(loadVersion);
    }
  }

  ngOnDestroy(): void {
    this.revokeObjectUrl();
    // Por si el componente se destruye con el panel de firma todavía abierto
    // (p. ej. al cerrar el modal contenedor), garantizamos que el scroll del
    // body quede restaurado y no afecte al resto de la app.
    this.unlockBodyScroll();
  }

  private revokeObjectUrl(): void {
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = undefined;
    }
  }

  /**
   * El panel de firma es un overlay propio (no usa MatDialog/CDK), por lo que
   * no bloquea automáticamente el scroll del fondo como sí lo hacen los
   * diálogos de Angular Material. Sin este bloqueo manual, al abrir el panel
   * se ven "por detrás" los scrollbars del layout de la app (humanage) tanto
   * horizontal como vertical, ya que el contenido de fondo sigue siendo
   * scrolleable debajo del backdrop semitransparente.
   */
  openSignaturePanel(): void {
    this.sigPanelExpanded = true;
    this.lockBodyScroll();
  }

  closeSignaturePanel(): void {
    this.sigPanelExpanded = false;
    this.unlockBodyScroll();
  }

  private lockBodyScroll(): void {
    if (document?.body) {
      document.body.style.overflow = 'hidden';
    }
  }

  private unlockBodyScroll(): void {
    if (document?.body) {
      document.body.style.overflow = '';
    }
  }

  private async refreshPDF(loadVersion: number) {
    let blob;
    try {
      if (this.file != null && this.file.length > 0) {
        const contentType = "application/pdf";
        blob = this.fileService.convertBase64ToBlob(this.file, contentType);
      } else if (this.fileBlob) {
        blob = this.fileBlob;
      } else {
        return;
      }
    } catch {
      if (loadVersion === this.pdfLoadVersion) {
        this.pdfSrc = null;
        this.loadError = true;
      }
      return;
    }
    this.size = blob.size;

    const blobURL = URL.createObjectURL(blob);
    if (loadVersion !== this.pdfLoadVersion) {
      URL.revokeObjectURL(blobURL);
      return;
    }
    this.objectUrl = blobURL;
    this.pdfSrc = blobURL;

    // Guardamos los bytes crudos para que estén disponibles cuando se dispare
    // afterLoadComplete() y se intente leer el certificado.
    try {
      this.rawPdfBytes = await blob.arrayBuffer();
    } catch {
      this.rawPdfBytes = undefined;
    }

    if (loadVersion !== this.pdfLoadVersion) {
      this.rawPdfBytes = undefined;
      return;
    }
    this.pdfViewerVisible = true;
  }

  zoom(zoomSize: number) {
    const actualZoom = this.pdfZoom + zoomSize;
    if (actualZoom > 0) {
      this.pdfZoom = actualZoom;
    }
  }

  showAllPages() {
    this.showAll = true;
  }

  printpdf() {
    if (!document || !document.body || !this.pdfSrc) {
      return;
    }
    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    iframe.src = this.pdfSrc;
    document.body.appendChild(iframe);
    let cleaned = false;
    const cleanup = () => {
      if (cleaned) {
        return;
      }
      cleaned = true;
      iframe.remove();
    };
    iframe.onload = () => {
      iframe.contentWindow?.print();
      if (this.doc != null) {
        this.auditService.auditPrintEmployeeDocument(this.doc, this.autoView);
      } else if (this.process != null) {
        this.auditService.auditPrintPaycheck(this.process, this.autoView);
      }
      setTimeout(cleanup, 1000);
    };
    iframe.onerror = cleanup;
    setTimeout(cleanup, 10000);

  }

  saveFile() {
    if(this.fileService.isPDF(null, this.fileName))
    {
      if (!document || !document.body)
      {
        return;
      }
      const a = document.createElement("a");
      document.body.appendChild(a);
      a.href = this.pdfSrc;
      a.download = this.fileName;
      a.click();
      a.remove();

      if (this.doc != null) {
        this.auditService.auditSaveEmployeeDocument(this.doc, this.autoView);
      } else if (this.process != null) {
        this.auditService.auditSavePaycheck(this.process, this.autoView);
      }
    }
     else{
    this.lsdServices.GetFileOriginal(this.fileId).
    subscribe(base64 =>
    {
        this.fileService.download(base64,this.fileName);
    });
  }
  }

  incrementPage(amount: number) {
    this.page += amount;
  }

  async afterLoadComplete(pdf: PDFDocumentProxy) {
    const loadVersion = this.pdfLoadVersion;
    if (!this.pdfSrc || this.loadError) {
      return;
    }
    this.pdf = pdf;
    this.isLoaded = true;
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await this.extractPdfSignatures(pdf as any, loadVersion);
    } catch {
      if (loadVersion === this.pdfLoadVersion) {
        this.pdfSignatures = [];
      }
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private async extractPdfSignatures(pdf: any, loadVersion: number): Promise<void> {
    const sigs: PdfSignatureInfo[] = [];

    for (let p = 1; p <= pdf.numPages; p++) {
      if (loadVersion !== this.pdfLoadVersion) {
        return;
      }
      const page = await pdf.getPage(p);
      const annotations = await page.getAnnotations();
      for (const ann of annotations) {
        if (ann.fieldType === 'Sig') {
          sigs.push({
            page: p,
            rect: ann.rect,
            hasAppearance: !!ann.hasAppearance,
            status: ann.hasAppearance ? 'valid' : 'no-appearance'
          });
        }
      }
    }

    // Enrich with app-level collaboration data
    const sigDataArr = this.doc?.documentFileSignaturesData || [];
    const collabSigs = this.getCollaborationSignatures();

    if (sigs.length === 0 && collabSigs.length > 0) {
      // No PDF-level sig widgets but document was signed via the system
      collabSigs.forEach(cs => {
        sigs.push({ page: 1, hasAppearance: false, status: 'valid', ...cs });
      });
    } else {
      sigs.forEach((sig, i) => {
        const sd = sigDataArr[i];
        if (sd) {
          sig.signer = sd.userName;
          sig.cuil = sd.cuil;
          sig.signedDate = sd.signedDate ? new Date(sd.signedDate) : undefined;
          sig.role = sd.signedBy;
        } else if (collabSigs[i]) {
          sig.signedDate = collabSigs[i].signedDate;
          sig.role = collabSigs[i].role;
        }

        if (!sig.cuil && sigs.length === 1 && this.doc?.employeeLegalId) {
          sig.cuil = this.doc.employeeLegalId;
        }
      });
    }

    await this.enrichWithCertificateData(sigs);
    if (loadVersion !== this.pdfLoadVersion) {
      return;
    }
    this.pdfSignatures = sigs;
  }

  /**
   * Enriquece cada firma con los datos del "certificado": si el PDF tiene un
   * certificado X.509 real embebido (CMS/PKCS7) se muestran sus datos reales
   * (emisor, titular, vigencia), parseando la estructura completa del PDF con
   * pdf-lib (igual que Acrobat: incluye object streams comprimidos). Si no
   * tiene ninguno —caso típico de firma electrónica interna sin PAdES— se
   * arma un dato equivalente con la información de negocio disponible.
   */
  private async enrichWithCertificateData(sigs: PdfSignatureInfo[]): Promise<void> {
    if (sigs.length === 0) {
      return;
    }

    let certs: PdfCertificateInfo[] = [];
    if (this.rawPdfBytes) {
      try {
        certs = await extractPdfCertificates(this.rawPdfBytes);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[pdf-wrapper] Error al parsear certificado(s) del PDF:', e);
      }
    } else {
      // eslint-disable-next-line no-console
      console.warn('[pdf-wrapper] No hay bytes crudos del PDF disponibles todavía; no se puede leer el certificado.');
    }

    if (certs.length === 0 && this.rawPdfBytes) {
      // eslint-disable-next-line no-console
      console.warn('[pdf-wrapper] No se encontró ningún diccionario de firma (/Contents + /ByteRange) con un CMS/PKCS7 válido en el PDF: ' +
        'el/los campo(s) de firma detectados no tienen un certificado digital real embebido ' +
        '(probablemente es solo un sello visual + registro de firma en la aplicación, sin PAdES). ' +
        'Se muestran los datos de firma electrónica disponibles como alternativa.');
    }

    sigs.forEach((sig, i) => {
      const cert = certs[i];
      if (cert) {
        sig.certSubject = cert.subjectCN || cert.subjectFull;
        sig.certIssuer = cert.issuerCN || cert.issuerFull;
        sig.certValidFrom = cert.validFrom;
        sig.certValidTo = cert.validTo;
        // "Firmado por" se sobrescribe SOLO si el /Name del diccionario de
        // firma del PDF trae un valor (dato declarado por la propia firma).
        // Si /Name no está presente, se respeta el dato de negocio ya
        // asignado (sd.userName) sin usar el certificado como fallback.
        if (cert.signerName) {
          sig.signer = cert.signerName;
        }
        // "Firma Digital" (en sentido legal, Ley 25.506) solo si el emisor del
        // certificado es una Autoridad Certificante LICENCIADA por la ONTI.
        // Un certificado X.509 real de un emisor no licenciado (p. ej.
        // "Autoridad Certificante CARDINAL") sigue teniendo certificado, pero
        // legalmente es Firma Electrónica, no Firma Digital.
        sig.hasEmbeddedCertificate = true;
        sig.isDigitalCertificate = !!cert.isLicensedIssuer;
      } else {
        this.fillFallbackCertificateData(sig);
      }
    });
  }

  /**
   * Cuando no hay certificado digital real, arma un "Titular"/"Emisor" a partir
   * de los datos de negocio ya disponibles (usuario que firmó, CUIL, rol),
   * dejando explícito que es una firma electrónica sin certificado X.509.
   */
  private fillFallbackCertificateData(sig: PdfSignatureInfo): void {
    if (sig.signer) {
      sig.certSubject = sig.cuil ? `${sig.signer} (CUIL/CUIT ${sig.cuil})` : sig.signer;
    }
    sig.certIssuer = sig.role
      ? this.transloco.translate('pdfWrapper.pdfWrapper.internalElectronicSignatureRoleLbl', { role: this.getRoleLabel(sig.role) })
      : this.transloco.translate('pdfWrapper.pdfWrapper.internalElectronicSignatureLbl');
    sig.hasEmbeddedCertificate = false;
    sig.isDigitalCertificate = false;
  }

  private getCollaborationSignatures(): Array<{ signedDate?: Date; role: string }> {
    const sigs: Array<{ signedDate?: Date; role: string }> = [];
    const lawyerDate = this.doc?.lawyerCollaborationData?.signatureDate;
    if (lawyerDate) {
      const d = new Date(lawyerDate);
      if (d.getFullYear() > 1900) sigs.push({ signedDate: d, role: 'Firmante' });
    }
    const empDate = this.doc?.employeeCollaborationData?.signatureDate;
    if (empDate) {
      const d = new Date(empDate);
      if (d.getFullYear() > 1900) sigs.push({ signedDate: d, role: 'Empleado' });
    }
    return sigs;
  }

  /** Etiqueta visible del rol de firma; el valor de `role` ('Firmante' / 'Empleado') no se modifica. */
  getRoleLabel(role: string): string {
    switch (role) {
      case 'Firmante':
        return this.transloco.translate('documentsToSign.signerLbl');
      case 'Empleado':
        return this.transloco.translate('documentsToSign.employeeLbl');
      default:
        return role;
    }
  }

  getInt(value: number): number {
    return Math.round(value);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onError(error: any) {
    this.error = error;
    this.loadError = true;
    this.pdfViewerVisible = false;
    this.isLoaded = false;
    this.pdfSignatures = [];
  }

  setPassword(password: string) {
    let newSrc;

    if (this.pdfSrc instanceof ArrayBuffer) {
      newSrc = { data: this.pdfSrc };
    } else if (typeof this.pdfSrc === 'string') {
      newSrc = { url: this.pdfSrc };
    } else {
      newSrc = { ...this.pdfSrc };
    }

    newSrc.password = password;

    this.pdfSrc = newSrc;
  }

}
